import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { GenerationForecastResponse } from './forecastTypes';
import { useTheme } from '@/hooks/useTheme';

interface Props {
  assetId: string;
  assetName?: string;
  targetTypeLabel?: string;
}

function fmtKwh(v: number | undefined): string {
  if (v == null) return '—';
  return Number.isInteger(v) ? `${v}` : v.toFixed(1);
}

function fmtDateLocal(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtShortDate(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

type AccuracyLevel = 'excellent' | 'good' | 'fair' | 'poor' | 'none';

function getAccuracyLevel(absPct: number | null | undefined): AccuracyLevel {
  if (absPct == null) return 'none';
  if (absPct <= 5) return 'excellent';
  if (absPct <= 10) return 'good';
  if (absPct <= 20) return 'fair';
  return 'poor';
}

function getAccuracyMeta(level: AccuracyLevel) {
  switch (level) {
    case 'excellent':
      return { labelKey: 'fw_excellent', color: 'var(--ok)', bg: 'rgba(22,163,74,0.1)', barColor: 'var(--ok)' };
    case 'good':
      return { labelKey: 'fw_good', color: 'var(--ok)', bg: 'rgba(101,163,13,0.1)', barColor: 'var(--ok)' };
    case 'fair':
      return { labelKey: 'fw_fair', color: 'var(--warn)', bg: 'rgba(217,119,6,0.1)', barColor: 'var(--warn)' };
    case 'poor':
      return { labelKey: 'fw_poor', color: 'var(--accent)', bg: 'var(--surface-2)', barColor: 'var(--accent)' };
    default:
      return { labelKey: '', color: 'var(--text-dim)', bg: 'rgba(155,155,164,0.08)', barColor: 'var(--line)' };
  }
}

function getDeltaColor(delta: number | null | undefined): string {
  if (delta == null) return 'var(--text-dim)';
  if (delta > 0) return 'var(--ok)';
  if (delta < 0) return 'var(--accent)';
  return 'var(--text-muted)';
}

export default function ForecastGenerationSummaryWidget({ assetId, assetName, targetTypeLabel = 'Sale / Export Forecast' }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<GenerationForecastResponse | null>(null);
  const [acc, setAcc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isDark } = useTheme();

  const cardBg = 'var(--surface-solid)';
  const cardBorder = 'var(--line)';
  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const mutedText = 'var(--text-muted)';
  const kpiBg = 'var(--surface-2)';
  const iconBg = 'var(--surface-2)';
  const tableHeaderBg = 'var(--surface-2)';
  const tableBorder = 'var(--line)';
  const footerBorder = 'var(--line)';
  const barTrack = 'var(--surface-2)';
  const pendingBg = 'var(--surface-2)';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.get<GenerationForecastResponse>(
          `/forecasting/assets/${assetId}/generation-forecasts/latest?includeHourly=false`
        );
        if (!cancelled) setData(result);
        const accResult = await api
          .get<any>(`/forecasting/assets/${assetId}/accuracy?days=7`)
          .catch(() => null);
        if (!cancelled) setAcc(accResult);
      } catch (err) {
        if (cancelled) return;
        const msg = getApiErrorMessage(err, 'Failed to load forecast summary');
        if (err instanceof ApiError && err.status === 404) {
          setError(null);
          setData(null);
        } else {
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  const HeaderSection = () => (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
        <i className="ri-sun-cloudy-line text-sm" style={{ color: 'var(--text-muted)' }}></i>
      </div>
      <div>
        <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('fw_summary_title')}</h3>
        <p className="text-xs" style={{ color: textMuted }}>{assetName} · {targetTypeLabel}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <HeaderSection />
        <div className="p-8 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
          <p className="text-sm mt-3" style={{ color: textMuted }}>Loading forecast summary...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <HeaderSection />
        <div className="p-8 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-error-warning-line text-xl" style={{ color: 'var(--accent)' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>Forecast summary is not available yet</p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data || !data.summary) {
    return (
      <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <HeaderSection />
        <div className="p-8 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-sun-cloudy-line text-xl" style={{ color: textMuted }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>Forecast summary is not available yet</p>
        </div>
      </div>
    );
  }

  const summary = data.summary;
  const daily = data.daily ?? [];

  const daysWithActual = daily.filter((d) => d.accuracyPct != null);
  const overallAccuracy = daysWithActual.length > 0
    ? daysWithActual.reduce((s, d) => s + (d.accuracyPct ?? 0), 0) / daysWithActual.length
    : null;
  const overallAbsError = daysWithActual.length > 0
    ? daysWithActual.reduce((s, d) => s + Math.abs(d.deltaPct ?? 0), 0) / daysWithActual.length
    : null;
  const overallAccuracyLevel = getAccuracyLevel(overallAbsError);

  const hasAnyActual = daily.some((d) => d.actualEnergyKwh != null);

  return (
    <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      <div className="flex items-center justify-between mb-5">
        <HeaderSection />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
          <div className="text-xs mb-1" style={{ color: textMuted }}>{t('fw_total_forecast')}</div>
          <div className="text-lg font-semibold" style={{ color: textMain }}>
            {fmtKwh(summary.totalForecastEnergyKwh)} <span className="text-xs font-normal" style={{ color: textMuted }}>kWh</span>
          </div>
        </div>
        <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
          <div className="text-xs mb-1" style={{ color: textMuted }}>{t('fw_max_hourly')}</div>
          <div className="text-lg font-semibold" style={{ color: textMain }}>
            {fmtKwh(summary.maxHourlyKwh)} <span className="text-xs font-normal" style={{ color: textMuted }}>kWh</span>
          </div>
        </div>
        <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
          <div className="text-xs mb-1" style={{ color: textMuted }}>{t('fw_window')}</div>
          <div className="text-sm font-semibold whitespace-nowrap" style={{ color: textMain }}>
            {fmtDateLocal(summary.firstForecastForTimestamp)} <span style={{ color: textMuted }}>→</span> {fmtDateLocal(summary.lastForecastForTimestamp)}
          </div>
        </div>
        {(() => {
          const accPct = acc?.accuracyPct ?? null;
          const lvl = getAccuracyLevel(acc?.nmaePct);
          const meta = getAccuracyMeta(lvl);
          const dc = acc?.decomposition;
          const reason = (accPct == null && dc)
            ? (dc.curtailmentHours > 0 ? 'станція під обмеженням (СКО/ОСР)'
               : dc.noActualHours > 0 ? 'немає фактичних даних'
               : 'недостатньо чистих годин')
            : null;
          return (
            <div className="p-3 rounded-lg" style={{ background: accPct != null ? meta.bg : kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>Точність прогнозу (nMAE, 7 дн)</div>
              <div className="flex items-center gap-2">
                {accPct != null ? (
                  <>
                    <span className="text-lg font-semibold" style={{ color: meta.color }}>{accPct}%</span>
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                      style={{ color: meta.color, background: meta.bg }}
                    >
                      {meta.labelKey ? t(meta.labelKey) : '—'}
                    </span>
                  </>
                ) : (
                  <span className="text-sm font-semibold" style={{ color: mutedText }}>—</span>
                )}
              </div>
              {accPct != null ? (
                <div className="text-[10px] mt-1" style={{ color: mutedText }}>
                  nMAE {acc.nmaePct}% · зміщення {acc.biasPct > 0 ? '+' : ''}{acc.biasPct}%
                </div>
              ) : reason ? (
                <div className="text-[10px] mt-1" style={{ color: mutedText }}>{reason}</div>
              ) : null}
              {dc && (
                <div className="text-[9px] mt-1.5 leading-snug" style={{ color: mutedText }}>
                  модель {dc.modelHours}г · недоступн. {dc.availabilityHours}г · обмеж. {dc.curtailmentHours}г · без факту {dc.noActualHours}г
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Daily table */}
      {daily.length === 0 ? (
        <div className="p-6 text-center">
          <p className="text-sm" style={{ color: textMuted }}>No forecast summary data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: tableHeaderBg }}>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>{t('fw_date')}</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Forecast kWh</th>
                {hasAnyActual && (
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Actual kWh</th>
                )}
                {hasAnyActual && (
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Δ kWh</th>
                )}
                {hasAnyActual && (
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Δ %</th>
                )}
                {hasAnyActual && (
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted, minWidth: 100 }}>{t('fw_accuracy')}</th>
                )}
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Max hourly kWh</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Avg cloud %</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Max SW W/m²</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase whitespace-nowrap" style={{ color: textMuted }}>Temp °C</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => {
                const hasActual = d.actualEnergyKwh != null;
                const deltaKw = d.deltaKwh;
                const deltaPct = d.deltaPct;
                const absPct = deltaPct != null ? Math.abs(deltaPct) : null;
                const accLevel = getAccuracyLevel(absPct);
                const accMeta = getAccuracyMeta(accLevel);
                const deltaColor = getDeltaColor(deltaKw);

                return (
                  <tr key={d.day} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                    <td className="px-3 py-2.5 whitespace-nowrap font-medium" style={{ color: textMain }}>
                      {fmtShortDate(d.day)}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium" style={{ color: textMain }}>
                      {fmtKwh(d.forecastEnergyKwh)}
                    </td>
                    {hasAnyActual && (
                      <td className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: hasActual ? textMain : mutedText }}>
                        {hasActual ? fmtKwh(d.actualEnergyKwh) : '—'}
                      </td>
                    )}
                    {hasAnyActual && (
                      <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium" style={{ color: deltaColor }}>
                        {deltaKw != null
                          ? `${deltaKw > 0 ? '+' : ''}${fmtKwh(deltaKw)}`
                          : '—'}
                      </td>
                    )}
                    {hasAnyActual && (
                      <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium" style={{ color: deltaColor }}>
                        {deltaPct != null
                          ? `${deltaPct > 0 ? '+' : ''}${deltaPct.toFixed(1)}%`
                          : '—'}
                      </td>
                    )}
                    {hasAnyActual && (
                      <td className="px-3 py-2.5">
                        {hasActual && d.accuracyPct != null ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: barTrack }}>
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(d.accuracyPct, 100)}%`, background: accMeta.barColor }}
                              />
                            </div>
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                              style={{ color: accMeta.color, background: accMeta.bg }}
                            >
                              {accMeta.labelKey ? t(accMeta.labelKey) : '—'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full" style={{ background: pendingBg }} />
                            <span
                              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap"
                              style={{ color: mutedText, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(155,155,164,0.08)' }}
                            >
                              Pending
                            </span>
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: textMuted }}>
                      {fmtKwh(d.maxHourlyKwh)}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: textMuted }}>
                      {d.avgCloudCover != null ? d.avgCloudCover.toFixed(1) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: textMuted }}>
                      {d.maxShortwaveRadiation != null ? Math.round(d.maxShortwaveRadiation) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap" style={{ color: textMuted }}>
                      {d.minTemperature2m != null && d.maxTemperature2m != null
                        ? `${d.minTemperature2m.toFixed(1)} — ${d.maxTemperature2m.toFixed(1)}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 pt-3 text-xs flex items-center gap-1.5 flex-wrap" style={{ color: textMuted, borderTop: `1px solid ${footerBorder}` }}>
        <span className="font-medium">Model:</span> {data.modelVersion ?? '—'} ·
        <span className="font-medium">Weather:</span> {data.inputWeatherProvider ?? '—'} ·
        <span className="font-medium">Target:</span> {data.targetType ?? '—'} ·
        <span className="font-medium">Updated:</span> {fmtDateLocal(data.forecastRunAt)}
        {hasAnyActual && (
          <>
            <span className="mx-1" style={{ color: 'var(--line-strong)' }}>|</span>
            <span className="font-medium">Days with actuals:</span> {daysWithActual.length}/{daily.length}
          </>
        )}
      </div>
    </div>
  );
}